import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard,
  HardHat,
  Hammer,
  ClipboardList,
  DollarSign,
  Headphones,
  BarChart3,
  Eye,
  Plus,
  FileText,
  Users,
  ChevronRight,
  Calendar,
  BookOpen,
  FileSearch,
  Wrench // Replaced Tool with Wrench
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ContractorSideNav = ({ isOpen }) => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const navStructure = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/contractor/dashboard',
      subItems: [
        { label: 'Project Analytics', path: '/contractor/dashboard/analytics', icon: BarChart3 },
        { label: 'Performance Metrics', path: '/contractor/dashboard/performance', icon: Eye }
      ]
    },
    {
      key: 'projects Requests',
      label: 'Projects',
      icon: HardHat,
      subItems: [
        { label: 'New Requests', path: '/contractor/projects/new-requests', icon: Plus },
      ]
    }, 
    {
      key: 'clients',
      label: 'Clients',
      icon: Users,
      subItems: [
        { label: 'Client Directory', path: '/contractor/clients/directory', icon: FileText },
        { label: 'Meetings', path: '/contractor/clients/meetings', icon: Calendar },
        { label: 'Bids & Proposals', path: '/contractor/clients/proposals', icon: FileSearch }
      ]
    },
    {
      key: 'crew',
      label: 'Crew Management',
      icon: Hammer,
      subItems: [
        { label: 'Team Members', path: '/contractor/crew/team', icon: Users },
        { label: 'Schedule', path: '/contractor/crew/schedule', icon: Calendar },
        { label: 'Equipment', path: '/contractor/crew/equipment', icon: Wrench } // Changed from Tool to Wrench
      ]
    },
    {
      key: 'finance',
      label: 'Finance',
      icon: DollarSign,
      subItems: [
        { label: 'Invoices', path: '/contractor/finance/invoices', icon: ClipboardList },
        { label: 'Expenses', path: '/contractor/finance/expenses', icon: DollarSign },
        { label: 'Payment Tracking', path: '/contractor/finance/payments', icon: BarChart3 }
      ]
    },
    {
      key: 'support',
      label: 'Support',
      icon: Headphones,
      subItems: [
        { label: 'Help Center', path: '/contractor/support/help', icon: Headphones },
        { label: 'Documentation', path: '/contractor/support/docs', icon: BookOpen }
      ]
    }
  ];

  const renderNavItem = (item) => {
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedSections[item.key];

    if (!hasSubItems && item.path) {
      return (
        <li key={item.key}>
          <NavLink
            to={item.path}
            className={({ isActive }) =>
              `flex items-center px-4 py-3 text-gray-800 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50 transition-all duration-200 rounded-r-lg mx-1 ${
                isActive ? 'bg-gradient-to-r from-gray-200 to-gray-100 font-medium border-l-3 border-gray-500 shadow-sm' : ''
              }`
            }
            aria-label={item.label}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {isOpen && <span className="ml-3 text-sm font-medium truncate">{item.label}</span>}
          </NavLink>
        </li>
      );
    }

    return (
      <li key={item.key}>
        <div
          className={`flex items-center px-4 py-3 text-gray-800 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50 transition-all duration-200 cursor-pointer rounded-r-lg mx-1 ${
            isExpanded ? 'bg-gradient-to-r from-gray-150 to-gray-75' : ''
          }`}
          onClick={() => isOpen && toggleSection(item.key)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              isOpen && toggleSection(item.key);
            }
          }}
        >
          <item.icon className="w-5 h-5 flex-shrink-0" />
          {isOpen && (
            <>
              <span className="ml-3 text-sm font-medium flex-1 truncate">{item.label}</span>
              {hasSubItems && (
                <motion.div
                  animate={{ rotate: isExpanded ? 90 : 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <ChevronRight className="w-4 h-4" />
                </motion.div>
              )}
            </>
          )}
        </div>

        <AnimatePresence>
          {isOpen && isExpanded && hasSubItems && (
            <motion.ul
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="overflow-hidden ml-1"
            >
              {item.subItems.map((subItem, subIndex) => (
                <li key={subIndex}>
                  <NavLink
                    to={subItem.path}
                    className={({ isActive }) =>
                      `flex items-center pl-8 pr-3 py-2 text-gray-700 text-sm hover:bg-gradient-to-r hover:from-white hover:to-gray-50 transition-all duration-200 rounded-r-lg ${
                        isActive ? 'bg-gradient-to-r from-white to-gray-100 font-medium border-l-2 border-gray-400 text-gray-900' : ''
                      }`
                    }
                    aria-label={subItem.label}
                  >
                    <subItem.icon className="w-4 h-4 flex-shrink-0 mr-2" />
                    <span className="truncate">{subItem.label}</span>
                  </NavLink>
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>
      </li>
    );
  };

  return (
    <motion.nav
      initial={{ width: isOpen ? 220 : 50 }}
      animate={{ width: isOpen ? 220 : 50 }}
      transition={{ type: 'spring', stiffness: 120, damping: 25 }}
      className={`bg-gradient-to-b from-gray-50 via-gray-100 to-gray-150 h-full shadow-lg flex flex-col border-r border-gray-200 ${
        isOpen ? 'w-[220px] sm:w-[240px] md:w-[270px]' : 'w-[50px] sm:w-[55px]'
      }`}
      role="navigation"
      aria-label="Contractor Sidebar Navigation"
    >
      {/* Navigation Links */}
      <ul className="flex-1 py-3 space-y-1">
        {navStructure.map(renderNavItem)}
      </ul>

      {/* Compact Footer */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="p-3 text-center border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100"
        >
          <p className="font-medium text-gray-800 text-sm">BUILD PRO</p>
          <p className="text-gray-500 text-sm">v1.0</p>
        </motion.div>
      )}
    </motion.nav>
  );
};

export default ContractorSideNav;